import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  ChangeDetectorRef,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Observable, Subscription, EMPTY, Subject, takeUntil } from 'rxjs';
import { MetaService } from '@app/shared/services/meta.service';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { WindowUtils } from '@app/shared/utils/window.util';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { AkitaAuthQuery } from '@app/akita/api/auth/state/auth.query';
import {
  Router,
  Event,
  ActivatedRoute,
  Params,
  NavigationEnd,
  RouterModule,
} from '@angular/router';
import { AuthOverlayDynamicService } from '@app/overlays/auth-overlay/services/auth-overlay-dynamic.service';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { GoogleAnalyticsService } from '@app/core/services/google-analytics.service';
import { logAuthDialogShown } from '@app/core/services/google-analytics.lazy';
import { Address } from '@app/akita/api/addresses/models/address.model';
import { LoadingComponent } from '@app/shared/components/loading/loading.component';
import { CommonModule } from '@angular/common';
import { PaymentOrderModel } from '@app/akita/api/checkout/models/payment-order.model';
import { CheckoutOrderSummaryComponent } from '../checkout-order-summary/checkout-order-summary.component';
import { AddressDetailsOverviewComponent } from '../address/address-details-overview/address-details-overview.component';
import { OrderTrackingStatusComponent } from '../order-tracking-status/order-tracking-status.component';
import { RateDeliveryExperienceComponent } from '../rate-delivery-experience/rate-delivery-experience.component';

@Component({
  selector: 'app-track-order',
  templateUrl: './track-order.feature.template.html',
  styleUrls: ['./track-order.feature.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    TranslocoModule,
    RateDeliveryExperienceComponent,
    AddressDetailsOverviewComponent,
    OrderTrackingStatusComponent,
    LoadingComponent,
    CheckoutOrderSummaryComponent,
  ],
})
export class TrackOrderComponent implements OnInit, OnChanges, OnDestroy {
  @Input() public mode?: 'seller' | 'buyer' | 'public_buyer' | null;
  @Input() public order?: PaymentOrderModel | null = null;
  @Input() public loading?: boolean | null = null;

  public isBrowser: boolean;
  public rtlMode: boolean;
  public params: Params = {};
  public queryParams: Params = {};
  public routeData: Params = {};

  public language: string;

  public lessThan$: Observable<ScreenSizeInfoState>;
  public biggerThan$: Observable<ScreenSizeInfoState>;

  public loggedUserId$: Observable<string | null>;
  public isCancelling$: Observable<boolean> = EMPTY;
  public productPicture$: Observable<string | null> = EMPTY;
  public deliveryAddress$: Observable<Address | null> = EMPTY;

  public userProfileUrlFragment$: Observable<Array<string>>;
  public queryParamsToKeep$: Observable<Params>;

  public showAuthOverlay: boolean;

  private authOverlaySubscription?: Subscription | null;
  private readonly langChangesObserver: Subscription | null;
  private readonly urlObserver: Subscription | null;
  private readonly subscriptions: Subscription;

  private readonly destroySubject: Subject<void>;

  constructor(
    private readonly meta: MetaService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly akitaAuthQuery: AkitaAuthQuery,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly authOverlayDynamicService: AuthOverlayDynamicService,
    private readonly translateService: TranslocoService,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly googleAnalyticsService: GoogleAnalyticsService
  ) {
    this.destroySubject = new Subject();
    this.subscriptions = new Subscription();
    this.isBrowser = this.akitaRouterQuery.isBrowser;
    this.lessThan$ = this.akitaScreenQuery.selectLessThan();
    this.biggerThan$ = this.akitaScreenQuery.selectBiggerThan();
    this.loggedUserId$ = this.akitaAuthQuery.selectUserID();
    this.userProfileUrlFragment$ = this.akitaRouterQuery.selectUserProfileUrlFragment();
    this.queryParamsToKeep$ = this.akitaRouterQuery.selectQueryParamsToKeep();

    this.language = '';

    this.rtlMode = false;
    this.showAuthOverlay = false;

    this.langChangesObserver = this.translateService.langChanges$
      .pipe(takeUntil(this.destroySubject))
      .subscribe({
        next: (lang) => {
          this.language = lang;
          const description = this.translateService.translate('title_delivery_details');
          this.meta.setTitle('title_delivery_details', true);
          this.meta.setCanonical();
          this.meta.setAlternateHreflangsTags(null);

          this.meta.setTags([
            { name: 'og:url', content: `${WindowUtils.getLocationHref() || ''}` },
            { name: 'og:type', content: `website` },
            { name: 'og:description', content: description },
            { name: 'description', content: description },
          ]);
        },
        error: () => {},
      });

    // Listen to Navigation End events
    this.urlObserver = this.router.events.pipe(takeUntil(this.destroySubject)).subscribe({
      next: ($event: Event) => {
        if ($event instanceof NavigationEnd) {
          let route = this.activatedRoute.snapshot;
          while (route.firstChild) {
            route = route.firstChild;
          }
          this.onNavigationEnd(
            route.params || {},
            route.queryParams || {},
            route.data || {}
          );
        }
      },
    });
  }

  public ngOnInit(): void {
    let route = this.activatedRoute.snapshot;
    while (route.firstChild) {
      route = route.firstChild;
    }
    this.onNavigationEnd(route.params || {}, route.queryParams || {}, route.data || {});
  }

  public onNavigationEnd(params: Params, queryParams: Params, routeData: Params): void {
    this.params = params || {};
    this.queryParams = queryParams || {};
    this.routeData = routeData || {};
    this.rtlMode = routeData.rtl || false;
  }

  public ngOnChanges(): void {
    this.changeDetector.detectChanges();
  }

  public openAuthDialog(mode?: string | null): boolean {
    if (!this.showAuthOverlay) {
      if (this.authOverlaySubscription) {
        this.authOverlaySubscription.unsubscribe();
      }

      this.authOverlaySubscription = this.authOverlayDynamicService.observe
        .pipe(takeUntil(this.destroySubject))
        .subscribe({
          next: (data?: any | null) => {
            this.showAuthOverlay = Boolean(data);
            this.changeDetector.markForCheck();
          },
        });

      logAuthDialogShown(this.googleAnalyticsService.logEventWrapper, 'track-order');
      this.authOverlayDynamicService.open({
        data: {
          mode: mode || 'auth-intro',
          successRedirect: this.deliveryInfoFragment,
        },
        isBrowser: this.isBrowser,
      });
    }
    return false;
  }

  private get homeUrlFragment(): Array<string> {
    return this.akitaRouterQuery.homeUrlFragment;
  }

  public get deliveryInfoFragment(): Array<string> {
    const fragment = this.homeUrlFragment;
    return fragment;
  }

  public getfaqFragment(): Array<string> {
    const fragment = this.homeUrlFragment;
    fragment.push('faq');
    return fragment;
  }

  public get inboxUrlFragment(): Array<string> {
    const fragment = this.homeUrlFragment;
    fragment.push('user', 'inbox');
    return fragment;
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();

    if (this.subscriptions) {
      this.subscriptions.unsubscribe();
    }

    if (this.langChangesObserver) {
      this.langChangesObserver.unsubscribe();
    }

    if (this.urlObserver) {
      this.urlObserver.unsubscribe();
    }
  }
}
